import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Crown, CheckCircle2, Sparkles, ArrowRight, 
  Building2, Palette, DollarSign, Mail, Copy, 
  Zap, Star, Gift
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useProducerAuth } from '../../context/ProducerAuthContext';
import confetti from 'canvas-confetti';

const BENEFITS = [
  { icon: Building2, title: 'Hasta 5 Venues', description: 'Crea múltiples planos para tus locaciones' },
  { icon: Palette, title: '9 Estilos Premium', description: 'Neón, Holográfico, Luxury y más' },
  { icon: DollarSign, title: 'Comisión 10%', description: 'Ahorra 5% en cada venta vs plan gratuito' },
  { icon: Mail, title: '1,000 Emails/mes', description: 'Campañas de marketing automatizadas' },
  { icon: Copy, title: 'Duplicar Eventos', description: 'Clona eventos y venues con un click' },
  { icon: Zap, title: 'Liquidaciones Express', description: 'Solicita hasta 50% anticipado' },
];

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshProducer, producer } = useProducerAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Trigger confetti celebration
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }
      const particleCount = 50 * (timeLeft / duration);
      
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#D4AF37', '#FFD700', '#FFA500', '#FFFFFF']
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#D4AF37', '#FFD700', '#FFA500', '#FFFFFF']
      });
    }, 250);

    // Refresh subscription status
    const checkSubscription = async () => {
      await refreshProducer();
      setLoading(false);
    };
    checkSubscription();

    return () => clearInterval(interval);
  }, [refreshProducer]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 overflow-hidden">
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-3xl" />
      </div>

      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-2xl w-full"
      >
        {/* Success Card */}
        <div className="bg-zinc-900/80 backdrop-blur-xl border border-[#D4AF37]/30 rounded-3xl p-8 md:p-12 text-center">
          
          {/* Crown Icon with glow */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", duration: 0.8, delay: 0.2 }}
            className="relative inline-block mb-6"
          >
            <div className="absolute inset-0 bg-[#D4AF37] rounded-full blur-2xl opacity-40 animate-pulse" />
            <div className="relative w-24 h-24 bg-gradient-to-br from-[#D4AF37] to-amber-600 rounded-full flex items-center justify-center shadow-2xl shadow-[#D4AF37]/30">
              <Crown className="text-black" size={48} />
            </div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.6 }}
              className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-4 border-zinc-900"
            >
              <CheckCircle2 className="text-white" size={16} />
            </motion.div>
          </motion.div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              ¡Bienvenido a
            </h1>
            <h2 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-[#D4AF37] via-yellow-400 to-amber-500 bg-clip-text text-transparent mb-4">
              Super Productora
            </h2>
            <p className="text-zinc-400 text-lg mb-2">
              Tu suscripción está activa
            </p>
            {producer && (
              <p className="text-[#D4AF37] font-medium">
                {producer.business_name}
              </p>
            )}
          </motion.div>

          {/* Sparkle divider */}
          <motion.div 
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.6 }}
            className="flex items-center justify-center gap-3 my-8"
          >
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
            <Sparkles className="text-[#D4AF37]" size={20} />
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
          </motion.div>

          {/* Benefits Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8"
          >
            {BENEFITS.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-4 hover:border-[#D4AF37]/30 transition-all group"
              >
                <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center mb-3 mx-auto group-hover:bg-[#D4AF37]/20 transition-colors">
                  <benefit.icon className="text-[#D4AF37]" size={20} />
                </div>
                <h3 className="font-semibold text-white text-sm mb-1">{benefit.title}</h3>
                <p className="text-zinc-500 text-xs">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="space-y-4"
          >
            <Button
              onClick={() => navigate('/producer/dashboard')}
              className="w-full md:w-auto px-8 py-6 bg-gradient-to-r from-[#D4AF37] to-amber-600 hover:from-[#C4A030] hover:to-amber-700 text-black font-bold text-lg rounded-xl shadow-lg shadow-[#D4AF37]/20 hover:shadow-[#D4AF37]/40 transition-all"
            >
              <span>Ir a mi Dashboard</span>
              <ArrowRight className="ml-2" size={20} />
            </Button>

            <p className="text-zinc-500 text-sm">
              Tu suscripción se renovará automáticamente cada mes
            </p>
          </motion.div>
        </div>

        {/* Footer badge */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="text-center mt-6"
        >
          <div className="inline-flex items-center gap-2 bg-zinc-900/60 backdrop-blur-sm border border-zinc-800 rounded-full px-4 py-2">
            <Gift className="text-[#D4AF37]" size={16} />
            <span className="text-zinc-400 text-sm">
              Gracias por confiar en <span className="text-white font-medium">Bravo</span>
            </span>
            <Star className="text-[#D4AF37]" size={16} />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccessPage;
